import { zodResolver } from '@hookform/resolvers/zod';
import { isAxiosError } from 'axios';
import { AlertCircle } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';

import { FormGroup } from '@/components/FormGroup';
import { Alert, AlertDescription } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { useChangePassword } from '@/hooks/useChangePassword';
import i18n from '@/lib/i18n';
import { ChangePasswordResponse } from '@/services/AuthService';

const passwordSchema = z
  .string()
  .min(6, {
    message: i18n.t(
      'components.ChangePasswordForm.validation.password.minLength',
    ),
  })
  .refine((password) => /[!@#$%^&*(),.?":{}|<>]/.test(password), {
    message: i18n.t(
      'components.ChangePasswordForm.validation.password.specialChar',
    ),
  })
  .refine((password) => /\d/.test(password), {
    message: i18n.t('components.ChangePasswordForm.validation.password.number'),
  })
  .refine((password) => /[a-z]/.test(password), {
    message: i18n.t(
      'components.ChangePasswordForm.validation.password.lowercase',
    ),
  })
  .refine((password) => /[A-Z]/.test(password), {
    message: i18n.t(
      'components.ChangePasswordForm.validation.password.uppercase',
    ),
  });

const schema = z
  .object({
    currentPassword: z
      .string()
      .min(
        1,
        i18n.t(
          'components.ChangePasswordForm.validation.currentPassword.required',
        ),
      ),
    newPassword: passwordSchema,
    confirmPassword: z.string(),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: i18n.t(
      'components.ChangePasswordForm.validation.password.mismatch',
    ),
    path: ['confirmPassword'],
  });

type FormData = z.infer<typeof schema>;

export function ChangePasswordForm() {
  const [feedback, setFeedback] = useState<null | {
    type: 'error' | 'success';
    message: string;
  }>(null);

  const {
    formState: { isSubmitting, errors },
    register,
    handleSubmit,
    reset,
  } = useForm<FormData>({
    resolver: zodResolver(schema),
  });

  const { t } = useTranslation('translation', {
    keyPrefix: 'components.ChangePasswordForm',
  });

  const { changePassword } = useChangePassword();

  const onSubmit = handleSubmit(async (data) => {
    try {
      const { message } = await changePassword({
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
      });

      setFeedback({
        type: 'success',
        message: t(`${message}`, { defaultValue: message }),
      });
      reset();
    } catch (error) {
      const errorMessage = t('updateError');

      if (isAxiosError<ChangePasswordResponse>(error) && error.status === 400) {
        setFeedback({
          type: 'error',
          message: error.response?.data.message ?? errorMessage,
        });
      }

      toast.error(errorMessage);
    }
  });

  return (
    <div className="max-w-md">
      <h3 className="mb-4 text-base font-medium">{t('title')}</h3>

      <form onSubmit={onSubmit} className="space-y-4">
        {feedback?.type === 'error' && (
          <Alert variant="destructive" className="py-2">
            <AlertDescription className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4" />
              {feedback.message}
            </AlertDescription>
          </Alert>
        )}

        {feedback?.type === 'success' && (
          <Alert className="border-green-200 bg-green-50 py-2 text-green-800">
            <AlertDescription>{feedback.message}</AlertDescription>
          </Alert>
        )}

        <FormGroup
          error={{ message: errors.currentPassword?.message, font: 'sm' }}
        >
          <Label htmlFor="current-password">{t('currentPasswordLabel')}</Label>
          <Input
            id="current-password"
            type="password"
            placeholder={t('currentPasswordPlaceholder')}
            {...register('currentPassword')}
          />
        </FormGroup>

        <FormGroup error={{ message: errors.newPassword?.message, font: 'sm' }}>
          <Label htmlFor="new-password">{t('newPasswordLabel')}</Label>
          <Input
            id="new-password"
            type="password"
            placeholder={t('newPasswordPlaceholder')}
            {...register('newPassword')}
          />
        </FormGroup>

        <FormGroup
          error={{ message: errors.confirmPassword?.message, font: 'sm' }}
        >
          <Label htmlFor="confirm-password">{t('confirmPasswordLabel')}</Label>
          <Input
            id="confirm-password"
            type="password"
            placeholder={t('confirmPasswordPlaceholder')}
            {...register('confirmPassword')}
          />
        </FormGroup>

        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? t('savingButton') : t('saveButton')}
        </Button>
      </form>
    </div>
  );
}
